/**
 * Lists-site shell UI strings (home / discover / chrome-adjacent).
 * Missing locale keys fall back to EN via listsUi(). Agent translations fill DE/JP overrides.
 */

import { CATALOG_COMPANY_LEGAL_NAME, type CatalogSiteLocale } from '../constants.js';
import { catalogUi } from './catalogUi.js';

export type ListsUiStrings = {
  documentTitle: string;
  documentTitleDiscover: string;
  documentTitleLogin: string;
  documentTitleAccount: string;
  documentTitleSettings: string;
  pageHeadingLogin: string;
  pageHeadingAccount: string;
  pageHeadingSettings: string;
  homeDescription: string;
  lead: string;
  searchPlaceholder: string;
  searchButton: string;
  featured: string;
  mostLiked: string;
  mostViewed: string;
  recent: string;
  showMore: string;
  loading: string;
  emptyFeatured: string;
  emptyMostLiked: string;
  emptyMostViewed: string;
  emptyRecent: string;
  loadError: string;
  discoverTitle: string;
  discoverSearchPlaceholder: string;
  sortLabel: string;
  sortRelevance: string;
  sortFeatured: string;
  sortLiked: string;
  sortViewed: string;
  sortRecent: string;
  sortName: string;
  listsHeading: string;
  collectionsHeading: string;
  noLists: string;
  noCollections: string;
  emptyCollections: string;
  pageStatus: (page: number, totalPages: number, found: number) => string;
  collectionsPageStatus: (page: number, totalPages: number, found: number) => string;
  previousPage: string;
  nextPage: string;
  paginationNav: string;
  pageNumberLabel: (page: number) => string;
  footerCatalog: string;
  footerDocs: string;
  footerEditor: string;
  footerGetApp: string;
  footerCompany: string;
  footerPrivacy: string;
  footerTos: string;
  footerImprint: string;
  footerReport: string;
  footerContact: string;
  footerCopyright: (year: number) => string;
};

const EN: ListsUiStrings = {
  documentTitle: 'Containd Spaces',
  documentTitleDiscover: 'Discover — Containd Spaces',
  documentTitleLogin: 'Sign in — Containd Spaces',
  documentTitleAccount: 'Your account — Containd Spaces',
  documentTitleSettings: 'Settings — Containd Spaces',
  pageHeadingLogin: 'Sign in',
  pageHeadingAccount: 'Your account',
  pageHeadingSettings: 'Settings',
  homeDescription: 'Search Spaces people published from Containd.',
  lead: 'Search for published Spaces.',
  searchPlaceholder: 'Search Spaces…',
  searchButton: 'Search',
  featured: 'Featured',
  mostLiked: 'Most liked',
  mostViewed: 'Most viewed',
  recent: 'New',
  showMore: 'Show more',
  loading: 'Loading…',
  emptyFeatured: 'No featured Spaces yet — search above or check New.',
  emptyMostLiked: 'No liked Spaces yet.',
  emptyMostViewed: 'No viewed Spaces yet.',
  emptyRecent: 'No public Spaces yet.',
  loadError: 'Could not load Spaces yet.',
  discoverTitle: 'Discover',
  discoverSearchPlaceholder: 'Search Spaces and collections…',
  sortLabel: 'Sort',
  sortRelevance: 'Relevance',
  sortFeatured: 'Featured',
  sortLiked: 'Most liked',
  sortViewed: 'Most viewed',
  sortRecent: 'Date added',
  sortName: 'Name',
  listsHeading: 'Spaces',
  collectionsHeading: 'User collections',
  noLists: 'No Spaces found.',
  noCollections: 'No public collections yet.',
  emptyCollections: 'No public collections yet.',
  pageStatus: (page, totalPages, found) =>
    `Page ${page} of ${totalPages} · ${found} Spaces`,
  collectionsPageStatus: (page, totalPages, found) =>
    `Page ${page} of ${totalPages} · ${found} collections`,
  previousPage: 'Previous',
  nextPage: 'Next',
  paginationNav: 'Pagination',
  pageNumberLabel: (page) => `Page ${page}`,
  footerCatalog: 'Catalog',
  footerDocs: 'Docs',
  footerEditor: 'Editor',
  footerGetApp: 'Get app',
  footerCompany: 'Company',
  footerPrivacy: 'Privacy',
  footerTos: 'Terms',
  footerImprint: 'Imprint',
  footerReport: 'Report content',
  footerContact: 'Contact',
  footerCopyright: (year) => `© ${year} ${CATALOG_COMPANY_LEGAL_NAME}`,
};

/** DE strings — full object so missing keys fail typecheck. */
const DE: ListsUiStrings = {
  ...EN,
  documentTitle: 'Containd Spaces',
  documentTitleDiscover: 'Entdecken — Containd Spaces',
  documentTitleLogin: 'Anmelden — Containd Spaces',
  documentTitleAccount: 'Ihr Konto — Containd Spaces',
  documentTitleSettings: 'Einstellungen — Containd Spaces',
  pageHeadingLogin: 'Anmelden',
  pageHeadingAccount: 'Ihr Konto',
  pageHeadingSettings: 'Einstellungen',
  homeDescription: 'Nach Spaces suchen, die Menschen aus Containd veröffentlicht haben.',
  lead: 'Suche nach veröffentlichten Spaces.',
  searchPlaceholder: 'Spaces suchen…',
  searchButton: 'Suchen',
  featured: 'Empfohlen',
  mostLiked: 'Beliebteste',
  mostViewed: 'Meistgesehen',
  recent: 'Neu',
  showMore: 'Mehr anzeigen',
  loading: 'Laden…',
  emptyFeatured: 'Noch keine empfohlenen Spaces — oben suchen oder Neu prüfen.',
  emptyMostLiked: 'Noch keine beliebten Spaces.',
  emptyMostViewed: 'Noch keine angesehenen Spaces.',
  emptyRecent: 'Noch keine öffentlichen Spaces.',
  loadError: 'Spaces konnten noch nicht geladen werden.',
  discoverTitle: 'Entdecken',
  discoverSearchPlaceholder: 'Spaces und Sammlungen suchen…',
  sortLabel: 'Sortierung',
  sortRelevance: 'Relevanz',
  sortFeatured: 'Empfohlen',
  sortLiked: 'Beliebteste',
  sortViewed: 'Meistgesehen',
  sortRecent: 'Datum',
  sortName: 'Name',
  listsHeading: 'Spaces',
  collectionsHeading: 'Benutzer-Sammlungen',
  noLists: 'Keine Spaces gefunden.',
  noCollections: 'Noch keine öffentlichen Sammlungen.',
  emptyCollections: 'Noch keine öffentlichen Sammlungen.',
  pageStatus: (page, totalPages, found) =>
    `Seite ${page} von ${totalPages} · ${found} Spaces`,
  collectionsPageStatus: (page, totalPages, found) =>
    `Seite ${page} von ${totalPages} · ${found} Sammlungen`,
  previousPage: 'Zurück',
  nextPage: 'Weiter',
  paginationNav: 'Seitenavigation',
  pageNumberLabel: (page) => `Seite ${page}`,
  footerCatalog: 'Katalog',
  footerDocs: 'Dokumentation',
  footerEditor: 'Editor',
  footerGetApp: 'App',
  footerCompany: 'Unternehmen',
  footerPrivacy: 'Datenschutz',
  footerTos: 'AGB',
  footerImprint: 'Impressum',
  footerReport: 'Inhalt melden',
  footerContact: 'Kontakt',
  footerCopyright: (year) => `© ${year} ${CATALOG_COMPANY_LEGAL_NAME}`,
};

/** JP strings — full object so missing keys fail typecheck. */
const JP: ListsUiStrings = {
  ...EN,
  documentTitle: 'Containd Spaces',
  documentTitleDiscover: '見つける — Containd Spaces',
  documentTitleLogin: 'サインイン — Containd Spaces',
  documentTitleAccount: 'アカウント — Containd Spaces',
  documentTitleSettings: '設定 — Containd Spaces',
  pageHeadingLogin: 'サインイン',
  pageHeadingAccount: 'アカウント',
  pageHeadingSettings: '設定',
  homeDescription: 'Containdから公開されたスペースを検索',
  lead: '公開されたスペースを検索。',
  searchPlaceholder: 'スペースを検索…',
  searchButton: '検索',
  featured: 'おすすめ',
  mostLiked: 'いいね順',
  mostViewed: '閲覧順',
  recent: '新着',
  showMore: 'もっと見る',
  loading: '読み込み中…',
  emptyFeatured: 'おすすめはまだありません — 上で検索するか新着を確認。',
  emptyMostLiked: 'いいねされたスペースはまだありません。',
  emptyMostViewed: '閲覧されたスペースはまだありません。',
  emptyRecent: '公開スペースはまだありません。',
  loadError: 'スペースを読み込めませんでした。',
  discoverTitle: '見つける',
  discoverSearchPlaceholder: 'スペースとコレクションを検索…',
  sortLabel: '並べ替え',
  sortRelevance: '関連度',
  sortFeatured: 'おすすめ',
  sortLiked: 'いいね順',
  sortViewed: '閲覧順',
  sortRecent: '追加日',
  sortName: '名前',
  listsHeading: 'スペース',
  collectionsHeading: 'ユーザーコレクション',
  noLists: 'スペースが見つかりません。',
  noCollections: '公開コレクションはまだありません。',
  emptyCollections: '公開コレクションはまだありません。',
  pageStatus: (page, totalPages, found) =>
    `${page} / ${totalPages}ページ · ${found}件のスペース`,
  collectionsPageStatus: (page, totalPages, found) =>
    `${page} / ${totalPages}ページ · ${found}件のコレクション`,
  previousPage: '前へ',
  nextPage: '次へ',
  paginationNav: 'ページネーション',
  pageNumberLabel: (page) => `${page}ページ`,
  footerCatalog: 'カタログ',
  footerDocs: 'ドキュメント',
  footerEditor: 'エディター',
  footerGetApp: 'アプリ',
  footerCompany: '会社情報',
  footerPrivacy: 'プライバシー',
  footerTos: '利用規約',
  footerImprint: 'インプリント',
  footerReport: 'コンテンツを報告',
  footerContact: 'お問い合わせ',
  footerCopyright: (year) => `© ${year} ${CATALOG_COMPANY_LEGAL_NAME}`,
};

const BY_LOCALE: Record<string, ListsUiStrings> = {
  en: EN,
  de: DE,
  jp: JP,
};

/** Resolve lists UI strings with EN fallback for unknown locales / missing keys. */
export function listsUi(locale: string): ListsUiStrings {
  const key = locale.toLowerCase().split('-')[0] ?? 'en';
  return BY_LOCALE[key] ?? EN;
}

/** Shared catalog chrome strings for the active locale (also EN-fallback). */
export function listsChromeUi(locale: string) {
  return catalogUi(locale);
}

export type ListsSiteLocale = CatalogSiteLocale;
